// $modelSlug.tsx
// Dynamic route for model-specific landing pages
// Examples:
//   /kling-2-6 → /create/video?model=kling-2-6 (slug, not full ID)
//   /flux-pro → /create/image?model=flux-pro
//   /kling-2-6-video-effects → /tools/video-effects?model=kling-2-6

import { createFileRoute, useNavigate, notFound } from "@tanstack/react-router"
import { useEffect } from "react"
import { slugToModelId, getModelType, isValidModelSlug } from "@aix/apiutils/src/models/modelSlugs"
import { Loader } from "@aix/ui"

// Suffix for video effects routes
const VIDEO_EFFECTS_SUFFIX = "-video-effects"

function ModelRedirectPage() {
  const { modelSlug } = Route.useParams()
  const navigate = useNavigate()

  useEffect(() => {
    // Check if this is a video-effects route
    const isVideoEffectsRoute = modelSlug.endsWith(VIDEO_EFFECTS_SUFFIX)
    const baseSlug = isVideoEffectsRoute ? modelSlug.slice(0, -VIDEO_EFFECTS_SUFFIX.length) : modelSlug

    // Look up the model ID from the slug to validate it exists
    const modelId = slugToModelId(baseSlug)

    if (!modelId) {
      // Not a valid model slug - redirect to 404
      void navigate({ to: "/404", replace: true })
      return
    }

    // Pass the slug (not the full model ID) for cleaner URLs
    // The target page will convert slug → model ID and store in localStorage
    if (isVideoEffectsRoute) {
      void navigate({
        to: "/tools/video-effects",
        search: { model: baseSlug },
        replace: true,
      })
    } else {
      const modelType = getModelType(modelId)

      if (modelType === "video") {
        void navigate({
          to: "/create/video",
          search: { model: baseSlug },
          replace: true,
        })
      } else if (modelType === "image") {
        void navigate({
          to: "/create/image",
          search: { model: baseSlug },
          replace: true,
        })
      } else {
        void navigate({ to: "/404", replace: true })
      }
    }
  }, [modelSlug, navigate])

  // Show loading while redirecting
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <Loader className="h-8 w-8" />
        <p className="text-sm text-muted-foreground">Loading model...</p>
      </div>
    </div>
  )
}

export const Route = createFileRoute("/$modelSlug")({
  component: ModelRedirectPage,
  // Use beforeLoad to validate the slug and throw 404 for invalid routes
  beforeLoad: ({ params }) => {
    const { modelSlug } = params

    // Check if this is a video-effects route
    const isVideoEffectsRoute = modelSlug.endsWith(VIDEO_EFFECTS_SUFFIX)
    const baseSlug = isVideoEffectsRoute ? modelSlug.slice(0, -VIDEO_EFFECTS_SUFFIX.length) : modelSlug

    // Validate the slug exists in our registry
    if (!isValidModelSlug(baseSlug)) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router uses non-Error throw for control flow
      throw notFound()
    }
  },
})
